import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { DateTime, Icon, Table, Tooltip } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useIsMounted } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import SyncLogDrawer from './SyncLogDrawer';

const REFRESH_DELAY_SECONDS = 5;
const REFRESH_IMPORT_STATUS_SECONDS = 10;

const Message = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 0;
  color: ${colors.grey55};
`;

const Small = styled.small`
  display: block;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
`;

const Title = styled.h3`
  font-size: 1.5rem;
  font-weight: ${weights.light};
`;

const RefreshButton = styled.div`
  margin-left: auto;
  padding-left: 1rem;
  color: ${({ disabled }) => (disabled ? colors.grey25 : colors.black)};
  cursor: pointer;
`;

const TableWrapper = styled.div`
  margin-top: 1rem;
`;

export default function SyncResults({ refreshDate }) {
  const api = useApi();
  const isMounted = useIsMounted();
  const { workspace } = useWorkspace();
  const [isLoading, setIsLoading] = useState(true);
  const [syncs, setSyncs] = useState([]);
  const [viewSync, setViewSync] = useState();
  const [canRefresh, setCanRefresh] = useState(true);

  const fetchSyncs = useCallback(async () => {
    try {
      const { data } = await api.www.workspaces(workspace.id).harvest.getSyncs();
      setSyncs(data);
    } finally {
      setIsLoading(false);
    }
  }, [api, workspace]);

  const isRunning = useMemo(() => _.filter(syncs, { isRunning: true }).length > 0, [syncs]);

  useEffect(() => {
    fetchSyncs();
    return fetchSyncs.cancel;
  }, [fetchSyncs, refreshDate]);

  useEffect(() => {
    let interval = null;
    if (isRunning) {
      interval = setInterval(fetchSyncs, REFRESH_IMPORT_STATUS_SECONDS * 1000);
    }
    return () => {
      if (interval) {
        clearTimeout(interval);
      }
    };
  }, [isRunning, fetchSyncs]);

  const refresh = () => {
    if (!canRefresh) {
      return;
    }
    setCanRefresh(false);
    fetchSyncs();
    setTimeout(() => {
      if (isMounted.current) {
        setCanRefresh(true);
      }
    }, REFRESH_DELAY_SECONDS * 1000);
  };

  if (isLoading) {
    return <PageLoader />;
  }
  if (syncs.length === 0) {
    return (
      <>
        <TitleRow>
          <Title>Migration Logs</Title>
        </TitleRow>
        <TableWrapper>
          <Message>No results available</Message>
        </TableWrapper>
      </>
    );
  }
  return (
    <>
      <TitleRow>
        <Title>Migration Logs</Title>
        <RefreshButton disabled={!canRefresh} onClick={refresh}>
          <Tooltip message="Refresh results">
            <Icon icon="sync" />
          </Tooltip>
        </RefreshButton>
      </TitleRow>
      <TableWrapper>
        <Table small>
          <Table.BoxHeader>
            <Table.Column>Date</Table.Column>
            <Table.Column>Status</Table.Column>
            <Table.Column width="9rem">Type</Table.Column>
            <Table.Column width="8rem">Duration</Table.Column>
            <Table.Column width="9rem">Changes</Table.Column>
          </Table.BoxHeader>
          <Table.Body>
            {syncs.map((sync) => (
              <Table.BoxRow
                key={sync.id}
                isDisabled={!sync.hasLog}
                onClick={() => (sync.hasLog ? setViewSync(sync.id) : null)}>
                <Table.Cell>
                  <DateTime value={sync.createdAt} format="MMM D, YYYY h:mm A" />
                </Table.Cell>
                <Table.Cell>{sync.status?.name || 'N/A'}</Table.Cell>
                <Table.Cell>
                  <p>
                    {sync.type?.name || 'N/A'}
                    <Small>{sync.fullSync ? 'Full' : 'Incremental'}</Small>
                  </p>
                </Table.Cell>
                <Table.Cell>{formatDuration(sync.startedAt, sync.endedAt)}</Table.Cell>
                <Table.Cell>{sync.changeCount ?? 'N/A'}</Table.Cell>
              </Table.BoxRow>
            ))}
          </Table.Body>
        </Table>
      </TableWrapper>
      {viewSync && <SyncLogDrawer id={viewSync} onClose={() => setViewSync()} />}
    </>
  );
}

function formatDuration(startedAt, endedAt) {
  if (!startedAt || !endedAt) return 'N/A';

  const diff = new Date(endedAt) - new Date(startedAt);
  const seconds = Math.floor((diff / 1000) % 60);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);

  let readable = '';
  if (hours > 0) readable += `${hours}h `;
  if (minutes > 0) readable += `${minutes}m `;
  if (diff < 1000) {
    readable += `${diff % 1000}ms`;
  } else if (seconds > 0) {
    readable += `${seconds}s`;
  }

  return readable.trim();
}
